import {
  IngestJobDetails,
  Job,
  JobArchiveResponse,
  JobArchiveState,
  JobResponse,
  JobStatus,
  JobType,
} from 'interfaces/job';
import {
  JobExperimentResultDetailedSummary,
  JobExperimentResultSummariesResponse,
} from 'interfaces/jobExperimentResults';
import { stringify } from 'qs';
import {
  BooleanParam,
  encodeQueryParams,
  JsonParam,
  NumberParam,
  QueryParamConfig,
  StringParam,
} from 'use-query-params';
import apiUtils from 'utils/api';
import { apiRequestHandlerPromise } from 'utils/apiRequestHandler';
import { sendAuthenticatedRequest } from 'utils/auth/authenticatedRequest';

export type SecondaryAnalysisFilter = 'primary' | 'secondary_all_attempts' | 'secondary_latest_attempt_per_slide';
export type JobAttemptSelection = 'latest_per_slide';

export interface JobFilters {
  studyId: string;
  archiveState?: JobArchiveState;
  jobIds?: string[];
  jobName?: string;
  orchestrationIds?: string[];
  primaryRunOrchestrationIds?: string[];
  secondaryAnalysisFilter?: SecondaryAnalysisFilter;
  orchestrationIdTextFilter?: string;
  users?: Record<string, string>[];
  senders?: string[];
  slideIds?: string[];
  statuses?: JobStatus[];
  types?: JobType[];
  flowClassNames?: string[];
  includePipelines?: boolean;
  childTasksOnly?: boolean;
  attemptSelection?: JobAttemptSelection;
}

export interface GetJobsParams {
  filters: JobFilters;
  labId: string;
  page?: number;
  pageSize?: number;
  withChildren?: boolean;
  childTasksOnly?: boolean;
  orderBy?: string;
}

export const getJobsParamsSchema: Record<keyof GetJobsParams, QueryParamConfig<any, any>> = {
  filters: JsonParam,
  labId: StringParam,
  page: NumberParam,
  pageSize: NumberParam,
  withChildren: BooleanParam,
  childTasksOnly: BooleanParam,
  orderBy: StringParam,
};

export interface GetJobsFilteredIdsParams {
  filters: JobFilters;
  labId: string;
}

export interface ImageAnalysisSlideJobStatus {
  slideId: string;
  jobType: JobType;
  jobId: string;
  orchestrationId?: string;
  status: JobStatus;
  startedAt?: string;
}

export interface ImageAnalysisStatusResponse {
  statuses: ImageAnalysisSlideJobStatus[];
}

export const getJobs = <JobType extends Job = Job>(params: GetJobsParams, signal?: AbortSignal) => {
  const stringParams = stringify(encodeQueryParams(getJobsParamsSchema, params));

  return apiRequestHandlerPromise<JobResponse<JobType>>({
    url: `jobs?${stringParams}`,
    method: 'GET',
    signal,
  });
};

export const getLatestJobByFilters = <JobType extends Job = Job>(
  params: Omit<GetJobsParams, 'page' | 'pageSize' | 'orderBy'>,
  signal?: AbortSignal
) => {
  return apiRequestHandlerPromise<JobType | null>({
    url: `jobs/latest.get`,
    method: 'POST',
    signal,
    data: JSON.stringify(params),
  });
};

export const getJobsFilteredIds = (params: GetJobsFilteredIdsParams, signal?: AbortSignal) => {
  return apiRequestHandlerPromise<string[]>({
    url: `jobs/filtered_ids.get`,
    method: 'POST',
    signal,
    data: JSON.stringify(params),
  });
};

export const getImageAnalysisStatus = (
  { studyId, slideIds, labId }: { studyId: string; slideIds: string[]; labId: string },
  signal?: AbortSignal
) =>
  apiRequestHandlerPromise<ImageAnalysisStatusResponse>({
    url: `jobs/image-analysis-status.get?labId=${encodeURIComponent(labId)}`,
    method: 'POST',
    signal,
    data: JSON.stringify({ studyId, slideIds }),
  });

export const getJob = <JobType extends Job = Job>(jobId: string, signal?: AbortSignal) => {
  return apiRequestHandlerPromise<JobType>({
    url: `jobs/${jobId}`,
    method: 'GET',
    signal,
  });
};

export const updateTmaCoreOverride = (
  labId: string,
  jobId: string,
  cores: Record<string, [number, number, number, number]>
) =>
  apiRequestHandlerPromise<Job>({
    url: `jobs/${jobId}/tma-core-override?labId=${encodeURIComponent(labId)}`,
    method: 'PUT',
    data: JSON.stringify({ cores }),
  });

export const deleteTmaCoreOverride = (labId: string, jobId: string) =>
  apiRequestHandlerPromise<Job>({
    url: `jobs/${jobId}/tma-core-override?labId=${encodeURIComponent(labId)}`,
    method: 'DELETE',
  });

export const archiveJob = (labId: string, jobId: string) => {
  return apiRequestHandlerPromise<JobArchiveResponse>({
    url: `jobs/${jobId}/archive?labId=${encodeURIComponent(labId)}`,
    method: 'POST',
  });
};

export const restoreJob = (labId: string, jobId: string) => {
  return apiRequestHandlerPromise<JobArchiveResponse>({
    url: `jobs/${jobId}/restore?labId=${encodeURIComponent(labId)}`,
    method: 'POST',
  });
};

export const getJobIngestDetails = (jobId: string, signal?: AbortSignal) => {
  return apiRequestHandlerPromise<IngestJobDetails>({
    url: `jobs/${jobId}/ingest_details.get`,
    method: 'GET',
    signal,
  });
};

export const getJobExperimentResultSummaries = (labId: string, jobIds: string[], signal?: AbortSignal) => {
  return apiRequestHandlerPromise<JobExperimentResultSummariesResponse>({
    url: `jobs/experiment_results_summaries.get?labId=${encodeURIComponent(labId)}`,
    method: 'POST',
    signal,
    data: JSON.stringify({ jobIds }),
  });
};

export const getJobExperimentResultSummary = (labId: string, jobId: string, signal?: AbortSignal) => {
  return apiRequestHandlerPromise<JobExperimentResultDetailedSummary>({
    url: `jobs/${jobId}/experiment_results.get?labId=${encodeURIComponent(labId)}`,
    method: 'GET',
    signal,
  });
};

export const archiveJobExperimentResults = (labId: string, jobId: string) => {
  return apiRequestHandlerPromise<JobExperimentResultDetailedSummary>({
    url: `jobs/${jobId}/experiment_results/archive?labId=${encodeURIComponent(labId)}`,
    method: 'POST',
  });
};

export const restoreJobExperimentResults = (labId: string, jobId: string) => {
  return apiRequestHandlerPromise<JobExperimentResultDetailedSummary>({
    url: `jobs/${jobId}/experiment_results/restore?labId=${encodeURIComponent(labId)}`,
    method: 'POST',
  });
};

export const getJobHtmlReport = async (jobId: string, signal?: AbortSignal): Promise<string> => {
  const response = await sendAuthenticatedRequest({
    endpoint: `jobs/${jobId}/html_report`,
    execute: (accessToken) =>
      apiUtils.request({
        url: `jobs/${jobId}/html_report`,
        method: 'GET',
        signal,
        data: null,
        accessToken,
        mediaType: 'text/plain',
        responseType: 'text',
      }),
  });

  return response.text || response.body || '';
};

export const cancelJob = (jobId: string, signal?: AbortSignal) => {
  return apiRequestHandlerPromise<Job>({
    url: `jobs/${jobId}/cancel`,
    method: 'POST',
    signal,
  });
};

interface JobChartUpdate {
  jobId: string;
  chartId: string;
  description: string;
}

interface NeighborhoodClustersInternalApprovalUpdate {
  jobId: string;
  clusteringResultIndex: number;
  internallyApproved: boolean;
}

export const editStudyReportJobChart = ({ jobId, chartId, description }: JobChartUpdate, signal?: AbortSignal) => {
  return apiRequestHandlerPromise<Job>({
    url: `jobs/${jobId}/charts/${chartId}`,
    method: 'PATCH',
    data: JSON.stringify({ description }),
    signal,
  });
};

export const updateNeighborhoodClustersJobInternalApproval = (
  { jobId, clusteringResultIndex, internallyApproved }: NeighborhoodClustersInternalApprovalUpdate,
  signal?: AbortSignal
) => {
  return apiRequestHandlerPromise<Job>({
    url: `jobs/${jobId}/neighborhood_clusters/${clusteringResultIndex}`,
    method: 'PATCH',
    data: JSON.stringify({ internallyApproved }),
    signal,
  });
};
